/**
 * auth/AuthContext.jsx
 *
 * Wraps the app with MsalProvider and exposes a React context that gives
 * any component:
 *   - user       — { id, name, email, role, initials, clinicEmails?, region? }
 *   - isLoading  — true while MSAL is initialising / fetching profile
 *   - getToken() — returns a fresh Graph API access token (silent, with
 *                  interactive fallback)
 *   - logout()   — signs the user out
 *
 * Role resolution order:
 *   1. Entra ID App Roles claim (idTokenClaims.roles) — takes precedence when
 *      roles are defined and assigned in the Entra ID app manifest.
 *   2. TherapieMaps reference data via GET /api/reference/clinics (server reads blob storage)
 *   3. SUPER_ADMIN_EMAILS lockout fallback
 *   4. Custom roles — a non-built-in Entra role string resolves provisionally
 *      (ReadOnly-shaped) until the governed roleDefinitions load, then either
 *      overlays its capabilities or is denied (fail closed)
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest, graphRequest, spRequest, apiRequest } from './msalConfig';
import { getTherapieMapsData } from '../api/blobClient.js';
import { armLoginPrefetch } from '../api/client.js';
import { setImpersonationProvider, clearSubSession } from '../api/apiFetch.js';
import { setBootStage } from '../lib/bootStage.js';
import LoginScreen from './LoginScreen.jsx';

// ── Context definition ─────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// ── Role model ─────────────────────────────────────────────────────────────────
// Labels, capability matrix and helpers live in roles.js (dependency-free so
// content code can import them without the MSAL stack); re-exported here so
// existing imports keep working.
import { ROLE_LABELS, attachCapabilities, isBuiltInRole, findCustomRoleDef } from './roles.js';
export { ROLE_LABELS, ROLE_CAPABILITIES, attachCapabilities, usersForAllRoles, getRoleLabel, resolveRolePriority, canAssumeRole } from './roles.js';
// Governed roleDefinitions bridge — AppConfigProvider mounts BELOW this
// provider, so custom-role definitions arrive via this module-level store
// rather than useAppConfig(). No import cycle: AppConfigContext only pulls
// from api/client + shared modules.
import { getLoadedRoleDefinitions, subscribeRoleDefinitions } from '../contexts/AppConfigContext.jsx';

// ── Impersonation ("assume identity") ──────────────────────────────────────────
// A Supervisor/SuperAdmin can view the app exactly as another (lesser) user —
// the full interactive UI, with every persisting action blocked at the API and
// the finishing flows short-circuited client-side. Persisted in sessionStorage
// so a page refresh during troubleshooting keeps the assumed view; cleared on
// tab close. The *real* user is always preserved in context so the "Exit" route
// can never be locked out, whatever role is assumed.
const IMPERSONATION_KEY = 'ae-impersonation';

function readScopeAnchor(obj) {
  const a = obj?.scopeAnchor;
  return a?.role && a?.email
    ? { role: a.role, email: String(a.email).trim().toLowerCase() }
    : null;
}

// Normalise a stored/incoming target. `subId` + `subName` are the clinic-mailbox
// PERSON being viewed as (see SubIdentityGate) and only mean anything there.
function normaliseImpersonation(obj) {
  if (!obj?.role || !obj?.email) return null;
  const scopeAnchor = readScopeAnchor(obj);
  const subId = obj.role === 'ClinicMailbox' && obj.subId ? String(obj.subId) : null;
  return {
    role: obj.role,
    email: String(obj.email).trim().toLowerCase(),
    ...(subId ? { subId, subName: obj.subName ? String(obj.subName) : null } : {}),
    ...(scopeAnchor ? { scopeAnchor } : {}),
  };
}

function readStoredImpersonation() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return normaliseImpersonation(raw ? JSON.parse(raw) : null);
  } catch { return null; }
}

/** Scope ceiling when switching identity while already impersonating. */
export function impersonationScopeAnchor(current) {
  if (!current?.role || !current?.email) return null;
  return readScopeAnchor(current) || { role: current.role, email: current.email };
}

// Build the effective (assumed) user from the real Supervisor/SuperAdmin + an impersonation
// target. Capabilities follow the assumed role IN FULL — including writes — so the
// UI renders exactly what that user sees and can open (composer, wizard, edit
// controls). Nothing can actually persist: the API rejects every non-GET while
// impersonating, and the finishing flows (post comment / submit case) short-circuit
// client-side with a notice. Only nested impersonation stays off. Targets are
// capped at lesser roles (never SuperAdmin; Supervisor only for a real SuperAdmin),
// enforced in the dialog and again server-side. Scope (which cases) is resolved
// server-side from the assumed email (or, for custom roles, the role's own scope
// definition), so clinic/region fields are cleared here. `roleDefinitions` lets an
// assumed custom role resolve its capability overlay.
function buildImpersonatedUser(realUser, impersonation, roleDefinitions) {
  const assumed = attachCapabilities({
    ...realUser,
    email:         impersonation.email,
    name:          impersonation.email,
    initials:      getInitials(impersonation.email),
    role:          impersonation.role,
    isSuperAdmin:  false,   // SuperAdmin is never assumable
    clinicEmails:  undefined,
    clinicObjects: undefined,
    region:        undefined,
  }, roleDefinitions);
  return {
    ...assumed,
    canImpersonate: false,  // no nested "assume identity"
    // View caps intersect with the real caller's (server does the same) —
    // assuming an identity must never reveal costs/private comments the
    // impersonator's own role hides from them.
    canSeeCosts:           assumed.canSeeCosts           && !!realUser.canSeeCosts,
    canSeePrivateComments: assumed.canSeePrivateComments && !!realUser.canSeePrivateComments,
    canSeeDashboard:       assumed.canSeeDashboard       && !!realUser.canSeeDashboard,
    canSeeKnowledge:       assumed.canSeeKnowledge       && !!realUser.canSeeKnowledge,
    canUseCaseReports:     assumed.canUseCaseReports     && !!realUser.canUseCaseReports,
    impersonating: true,
    // Clinic mailbox: WHO at that shared login is being viewed as (null until
    // picked) — the shared account name means nothing on its own.
    subIdentityName: impersonation.subName || null,
    realUser: { name: realUser.name, email: realUser.email, role: realUser.role },
  };
}

// ── SuperAdmin gate ────────────────────────────────────────────────────────────
// Temporary email-based gate while the admin panel is in development.
// Replace with a proper Entra app role ('SuperAdmin') before wider rollout.
const SUPER_ADMIN_EMAILS = new Set(['mariusc@therapieclinic.com']);

// ── Role resolution ────────────────────────────────────────────────────────────
// Precedence:
//   1. Entra App Roles (idTokenClaims.roles) — authoritative once configured
//   2. TherapieMaps blob lookup (cmByMail / horoByMail)
//   3. Fallback 'Admin' — full access while roles are not yet assigned
//
// Returns an object: { role, clinicEmails?, clinicObjects?, region? }

// apiRoles: decoded from the API access token (audience = Functions app).
// App roles are defined on the Functions API app, so they appear there, not in the ID token.
async function loadTherapieMaps(apiAccessToken) {
  if (!apiAccessToken) return null;
  try {
    return await getTherapieMapsData({ accessToken: apiAccessToken });
  } catch (err) {
    console.warn('[AuthContext] TherapieMaps lookup failed:', err.message);
    return null;
  }
}

async function resolveRole(account, apiRoles = [], apiAccessToken = null) {
  const email = (account?.username || '').toLowerCase();
  const roles = apiRoles;

  // 1️⃣  Entra App Roles — fast path (no TherapieMaps round-trip).
  if (roles.includes('SuperAdmin'))     return { role: 'SuperAdmin',     isSuperAdmin: true  };
  if (roles.includes('Supervisor'))     return { role: 'Supervisor',     isSuperAdmin: false };
  if (roles.includes('SupervisorView')) return { role: 'SupervisorView', isSuperAdmin: false };
  if (roles.includes('Admin'))          return { role: 'Admin',          isSuperAdmin: false };
  // ClinicMailbox — a shared clinic login. The SubIdentityGate resolves the
  // clinic + roster from /api/clinic/context; nothing here needs TherapieMaps.
  if (roles.includes('ClinicMailbox'))  return { role: 'ClinicMailbox',  isSuperAdmin: false };
  if (roles.includes('ReadOnly'))       return { role: 'ReadOnly' };

  // TherapieMaps is only needed for HoRO/CM enrichment or users without an Entra role.
  const needsTm = roles.includes('HoRO') || roles.includes('ClinicManager')
    || (!roles.length && !SUPER_ADMIN_EMAILS.has(email));
  const tm = needsTm ? await loadTherapieMaps(apiAccessToken) : null;

  if (roles.includes('HoRO')) {
    const horo = tm?.horoByMail?.[email];
    const region = horo?.region || tm?.assignment?.region || tm?.scope?.region || null;
    // Secondary region (RegionSec) rides along for display; the server grants
    // the Region ∪ RegionSec scope regardless of what the client shows.
    return {
      role:    'HoRO',
      region,
      regions: [...new Set([region, horo?.regionSec].filter(Boolean))],
    };
  }
  if (roles.includes('ClinicManager')) {
    const staff = tm?.cmByMail?.[email] || tm?.amByMail?.[email];
    const clinicObjects = staff?.clinicObjects?.length
      ? staff.clinicObjects
      : (tm?.assignment?.clinicObjects || []);
    return {
      role:          'ClinicManager',
      clinicEmails:  clinicObjects.map(c => (c.email || '').toLowerCase()).filter(Boolean),
      clinicObjects,
    };
  }

  // 2️⃣  TherapieMaps lookup (for users not yet assigned an Entra role)
  if (tm) {
    const staff = tm.cmByMail[email] || tm.amByMail?.[email];
    if (staff) {
      const clinicObjects = staff.clinicObjects?.length
        ? staff.clinicObjects
        : (tm?.assignment?.clinicObjects || []);
      return {
        role:          'ClinicManager',
        clinicEmails:  (staff.designatedClinic || []).map(e => e.toLowerCase()),
        clinicObjects,
      };
    }

    const horo = tm.horoByMail[email];
    if (horo) {
      return { role: 'HoRO', region: horo.region || null };
    }
  }

  // 3️⃣  Lockout protection for super admins only — no domain-wide fallback.
  if (SUPER_ADMIN_EMAILS.has(email)) return { role: 'SuperAdmin', isSuperAdmin: true };

  // 4️⃣  Custom roles — any remaining Entra role string resolves provisionally.
  // Capabilities overlay from the governed roleDefinitions once config loads
  // (attachCapabilities); no enabled definition ⇒ treated as Unauthorized.
  // Deliberately AFTER the lockout fallback so lockout outranks custom roles,
  // and no TherapieMaps round-trip — custom scope is server-resolved from the
  // role's own scope definition.
  const customRole = roles.find(r => !isBuiltInRole(r));
  if (customRole) return { role: customRole };

  // 5️⃣  No Entra role, not in TherapieMaps — deny access.
  return { role: 'Unauthorized' };
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

/** Don't let hung API/DB calls block the warming screen indefinitely. */
function warmupWithTimeout(promise, ms, label) {
  if (!promise) return Promise.resolve(undefined);
  return Promise.race([
    promise.catch(e => {
      console.warn(`[AuthContext] ${label} failed:`, e.message);
    }),
    new Promise(resolve => {
      setTimeout(() => {
        console.warn(`[AuthContext] ${label} timed out after ${ms}ms — continuing`);
        resolve(undefined);
      }, ms);
    }),
  ]);
}

// ── Inner provider (requires MsalProvider ancestor) ───────────────────────────

function AuthInner({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [user, setUser]         = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState(null);
  const [impersonation, setImpersonationState] = useState(readStoredImpersonation);
  const impersonationRef = useRef(impersonation);

  // Governed roleDefinitions (custom roles) — published by AppConfigProvider,
  // which mounts below this provider once children render. null until the
  // config has resolved (or failed → the built-in seed). Custom-role users
  // render ReadOnly-shaped until then; a ref mirrors the latest value for the
  // async profile-load paths.
  const [roleDefs, setRoleDefs] = useState(getLoadedRoleDefinitions);
  const roleDefsRef = useRef(roleDefs);
  roleDefsRef.current = roleDefs;
  useEffect(() => subscribeRoleDefinitions(defs => setRoleDefs(defs)), []);

  // Re-attach capabilities when the definitions arrive/change — upgrades a
  // provisional custom-role user, and downgrades one whose role was disabled.
  useEffect(() => {
    if (!roleDefs) return;
    setUser(prev => (prev ? attachCapabilities(prev, roleDefs) : prev));
  }, [roleDefs]);

  // Register before login prefetch — App mounts later, after the warming screen.
  impersonationRef.current = impersonation;
  setImpersonationProvider(() => impersonationRef.current);

  // Sign-in is now user-initiated via the LoginScreen SSO button.
  function signIn() {
    setLoginError(null);
    instance.loginRedirect(loginRequest).catch(err => {
      console.error('[AuthContext] loginRedirect failed:', err);
      setLoginError(err.message || 'Sign-in failed. Please try again.');
    });
  }

  // ── Re-authentication ──────────────────────────────────────────────────────────
  // Single entry point for "the session is dead, send the user back through login".
  // Used by the token getters (silent renewal failed), the bootstrap profile load,
  // and apiFetch's 401 backstop. A full loginRedirect (not acquireTokenRedirect) is
  // the most robust recovery: it re-establishes the login session even when the
  // refresh token / SSO cookie has lapsed, and returns the user to the same URL.
  // The ref guard ensures concurrent failures fire exactly one redirect (a second
  // interactive request would throw MSAL's "interaction_in_progress").
  const reauthStartedRef = useRef(false);
  function triggerReauth() {
    if (reauthStartedRef.current) return;
    reauthStartedRef.current = true;
    instance.loginRedirect(loginRequest).catch(err => {
      reauthStartedRef.current = false;
      console.error('[AuthContext] Re-auth redirect failed:', err);
    });
  }

  // Acquire an access token for `request`, silently if possible. On expiry that
  // requires interaction, kick off the re-auth redirect and throw — the page is
  // navigating away, so callers must NOT proceed with a missing/stale token
  // (that path produced the "Unauthorized: no token provided" dead end).
  async function acquireToken(request) {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) throw new Error('No active MSAL account');
    try {
      const resp = await instance.acquireTokenSilent({ ...request, account });
      return resp.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        triggerReauth();
      }
      throw err;
    }
  }

  // Once we have an account, fetch /me from Graph for a richer profile
  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    const account = accounts[0];
    instance.setActiveAccount(account);

    let cancelled = false;

    async function loadProfile() {
      // Silent token renewal is the canary for session validity. Because tokens
      // live in sessionStorage they survive a page refresh, so MSAL still reports
      // the user as authenticated even after the underlying session has expired.
      // If we can't renew silently, the session is genuinely dead — re-authenticate
      // rather than degrading to a shell built from stale cached account fields
      // (which left the user "logged in" but unable to load any data).
      let tokenResp;
      try {
        tokenResp = await instance.acquireTokenSilent({ ...graphRequest, account });
      } catch (err) {
        console.warn('[AuthContext] Silent token renewal failed — re-authenticating:', err.message);
        setBootStage('auth.token.graph', 'Session expired', err.message);
        if (!cancelled) triggerReauth();
        return;
      }

      try {
        setLoading(true);
        setBootStage('auth.profile', 'Connecting to AE Tracker', 'Loading your Microsoft profile');

        const graphToken = tokenResp.accessToken;

        // Graph profile + API token in parallel. Profile photo loads in the
        // background — it must never block the warming screen.
        const [meResp, apiResp] = await Promise.all([
          fetch('https://graph.microsoft.com/v1.0/me', {
            headers: { Authorization: `Bearer ${graphToken}` },
          }),
          instance.acquireTokenSilent({ ...apiRequest, account }).catch(err => {
            console.warn('[AuthContext] Could not acquire API token for role resolution:', err.message);
            return null;
          }),
        ]);

        if (!meResp.ok) throw new Error(`Graph /me returned ${meResp.status}`);
        const me = await meResp.json();

        // Profile photo — must not block the warming screen. Hold the URL in a
        // local so setUser() below picks it up even when the fetch wins the race.
        let profilePhotoUrl = null;
        fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${graphToken}` },
        })
          .then(async (photoResp) => {
            if (!photoResp.ok) {
              if (photoResp.status !== 404) {
                console.warn('[AuthContext] Profile photo fetch failed:', photoResp.status);
              }
              return;
            }
            profilePhotoUrl = URL.createObjectURL(await photoResp.blob());
            if (!cancelled) {
              setUser(prev => (prev ? { ...prev, photoUrl: profilePhotoUrl } : prev));
            }
          })
          .catch(err => {
            console.warn('[AuthContext] Profile photo fetch failed:', err.message);
          });

        let apiRoles = [];
        let apiAccessToken = apiResp?.accessToken || null;
        if (!apiAccessToken) {
          console.warn('[AuthContext] No API token — re-authenticating for API scope');
          setBootStage('auth.token.api', 'API access required', 'Re-authenticating for API scope');
          if (!cancelled) triggerReauth();
          return;
        }
        if (apiAccessToken) {
          const parts = apiAccessToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            apiRoles = Array.isArray(payload.roles) ? payload.roles : [];
          }
        }

        // Warm the All Cases critical path in parallel; dashboard summary is non-blocking.
        const prefetch = apiAccessToken ? armLoginPrefetch(apiAccessToken) : null;
        prefetch?.dashSummary?.catch(e => {
          console.warn('[AuthContext] Dashboard summary prefetch failed:', e.message);
        });
        const WARMUP_MS = 20_000;
        setBootStage('auth.warmup', 'Connecting to AE Tracker', 'Loading cases and database stats');
        const roleInfo = await Promise.all([
          warmupWithTimeout(prefetch?.listFirst, WARMUP_MS, 'Cases list prefetch'),
          warmupWithTimeout(prefetch?.stats, WARMUP_MS, 'DB warmup stats'),
          (async () => {
            setBootStage('auth.role', 'Connecting to AE Tracker', 'Resolving your role and clinic scope');
            return resolveRole(account, apiRoles, apiAccessToken);
          })(),
        ]).then(([, , resolved]) => resolved);
        const email    = (me.mail || me.userPrincipalName || '').toLowerCase();
        // isSuperAdmin: from Entra role claim (preferred) or fallback email list
        const isSuperAdmin = roleInfo.isSuperAdmin ?? SUPER_ADMIN_EMAILS.has(email);

        if (!cancelled) {
          setBootStage('auth.ready', 'Signed in', `${email} · ${roleInfo.role || 'Unknown role'}`);
          setUser(attachCapabilities({
            id:       me.id,
            name:     me.displayName || account.name || 'Unknown',
            email,
            initials: getInitials(me.displayName || account.name),
            photoUrl: profilePhotoUrl,
            isSuperAdmin,
            ...roleInfo,
          }, roleDefsRef.current));
        }
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          setBootStage('auth.token.graph', 'Session expired', 'Interactive sign-in required');
          triggerReauth();
        } else {
          console.error('[AuthContext] Failed to load user profile:', err);
          setBootStage('auth.profile', 'Profile load failed', err.message);
          // Degrade gracefully using cached MSAL account fields
          const roleInfo = await resolveRole(account, []).catch(() => ({ role: 'Unauthorized' }));
          const fbEmail = (account.username || '').toLowerCase();
          if (!cancelled) {
            setUser(attachCapabilities({
              id:           account.localAccountId,
              name:         account.name || 'Unknown',
              email:        fbEmail,
              initials:     getInitials(account.name),
              isSuperAdmin: SUPER_ADMIN_EMAILS.has(fbEmail),
              ...roleInfo,
            }, roleDefsRef.current));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadProfile();
    return () => { cancelled = true; };
  }, [isAuthenticated, accounts, instance]);

  // ── getToken ──────────────────────────────────────────────────────────────────
  // Use this in client.js to get a bearer token for all Graph API calls.

  async function getToken() {
    return acquireToken(graphRequest);
  }

  // ── getApiToken ───────────────────────────────────────────────────────────────
  // Bearer token for the AE Tracker Functions API (audience = Functions app ID).

  async function getApiToken() {
    return acquireToken(apiRequest);
  }

  // ── getSpToken ────────────────────────────────────────────────────────────────
  // Bearer token for the SharePoint REST API (different resource from Graph).

  async function getSpToken() {
    return acquireToken(spRequest);
  }

  // ── logout ────────────────────────────────────────────────────────────────────

  function logout() {
    clearSubSession();   // never carry a clinic sub-identity across a re-login
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  // ── Impersonation ("assume identity") handlers ─────────────────────────────────

  function setImpersonation(target) {
    const next = normaliseImpersonation(target);
    setImpersonationState(next);
    try {
      if (next) sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(next));
      else      sessionStorage.removeItem(IMPERSONATION_KEY);
    } catch { /* sessionStorage unavailable — in-memory only */ }
  }
  const exitImpersonation = () => setImpersonation(null);

  // Point an assumed CLINIC MAILBOX view at one of its people (SubIdentityGate).
  // A shared mailbox owns no cases, so this is what makes the view show anything;
  // `null` returns to the picker. No PIN is involved — the server re-checks the
  // person against that clinic's active roster on every request.
  function setImpersonationSubIdentity(entry) {
    const current = impersonationRef.current;
    if (!current) return;
    setImpersonation({
      ...current,
      subId: entry?.subId || null,
      subName: entry?.displayName || entry?.subName || null,
    });
  }

  // Honour impersonation ONLY for Supervisors and Super Admins. This guards against a stale
  // sessionStorage entry and is the backstop that prevents lockout: a non-supervisory
  // (or the assumed user) can never *start* impersonating, and exit is always
  // a local clear that needs no backend access.
  const canImpersonate      = !!user?.canImpersonate;
  const activeImpersonation = canImpersonate ? impersonation : null;

  // Stable getter for apiFetch's impersonation provider (reads latest via ref).
  const getImpersonation = useCallback(() => impersonationRef.current, []);

  // Mirror high-level auth UI state into boot stages for crash diagnostics.
  useEffect(() => {
    if (!isAuthenticated) {
      setBootStage('auth.signed-out', 'Waiting for sign-in');
      return;
    }
    if (inProgress !== InteractionStatus.None) {
      setBootStage('auth.redirecting', 'Redirecting to Microsoft');
      return;
    }
    if (isLoading) return;
    if (user?.role === 'Unauthorized') {
      setBootStage('auth.denied', 'Access denied', user?.email || null);
      return;
    }
    if (user) {
      setBootStage('auth.ready', 'Signed in', `${user.email} · ${user.role}`);
    }
  }, [isAuthenticated, inProgress, isLoading, user?.role, user?.email]);

  // ── Login / warmup screen ────────────────────────────────────────────────────

  // Not signed in — show the SSO sign-in page (or the "redirecting…" state
  // while MSAL is mid-redirect through login.microsoftonline.com).
  if (!isAuthenticated) {
    if (inProgress !== InteractionStatus.None) {
      return <LoginScreen mode="redirecting" />;
    }
    return (
      <LoginScreen
        mode="signed-out"
        onSignIn={signIn}
        error={loginError}
      />
    );
  }

  // Signed in but MSAL is mid-interaction (re-auth redirect, handleRedirect, …).
  if (inProgress !== InteractionStatus.None) {
    const account = accounts[0];
    const name = account?.name || account?.username || 'Signed in';
    return (
      <LoginScreen
        mode="redirecting"
        account={{
          name,
          email: (account?.username || '').toLowerCase(),
          initials: getInitials(name),
        }}
      />
    );
  }

  // Signed in, still loading profile / warming the DB.
  if (isLoading) {
    const account = accounts[0];
    const fallbackName  = account?.name || account?.username || 'Signed in';
    const fallbackEmail = (account?.username || '').toLowerCase();
    return (
      <LoginScreen
        mode="warming"
        account={{
          name:     fallbackName,
          email:    fallbackEmail,
          initials: getInitials(fallbackName),
        }}
        onCancel={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
      />
    );
  }

  // Authenticated but no assigned role — block with a clear sign-out path.
  // A custom (non-built-in) Entra role only lands here once roleDefinitions
  // have resolved and still hold no enabled definition for it. Until then the
  // user renders ReadOnly-shaped — all capabilities false, display-only, every
  // endpoint re-derives authz server-side — so AppConfigProvider can mount
  // and actually load the definitions (it sits below this provider).
  const customRoleDenied = !!user && !isBuiltInRole(user.role)
    && roleDefs !== null && !findCustomRoleDef(user.role, roleDefs);
  if (user?.role === 'Unauthorized' || customRoleDenied) {
    return (
      <LoginScreen
        mode="unauthorized"
        account={{
          name:     accounts[0]?.name || 'Signed in',
          email:    (accounts[0]?.username || '').toLowerCase(),
          initials: getInitials(accounts[0]?.name || ''),
        }}
        onCancel={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
      />
    );
  }

  // Effective user precedence:
  //   1. Impersonation — read-only view as another user (scope resolved server-side
  //      from the assumed email; the real SuperAdmin is preserved as `realUser`).
  //   2. The real signed-in user.
  let effectiveUser = user;
  if (user && activeImpersonation) {
    effectiveUser = buildImpersonatedUser(user, activeImpersonation, roleDefs);
  }

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      realUser: user,
      isImpersonating: !!activeImpersonation,
      impersonation: activeImpersonation,
      canImpersonate,
      setImpersonation,
      setImpersonationSubIdentity,
      exitImpersonation,
      getImpersonation,
      isLoading, getToken, getApiToken, getSpToken, triggerReauth, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Public export ──────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthInner>{children}</AuthInner>
    </MsalProvider>
  );
}
